import type { AppStorage, Profile, Account } from '@yada/types';

// Cache open DB connections so we don't re-open on every operation
const dbCache = new Map<string, Promise<IDBDatabase>>();

function openAppDB(namespace: string): Promise<IDBDatabase> {
  const dbName = `yada-app-${namespace}`;
  if (!dbCache.has(dbName)) {
    dbCache.set(dbName, new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore('keyval');
      };
      req.onsuccess = () => {
        const db = req.result;
        // Lets "clear local data" delete this database without being blocked.
        db.onversionchange = () => {
          db.close();
          dbCache.delete(dbName);
        };
        resolve(db);
      };
      req.onerror = () => {
        dbCache.delete(dbName);
        reject(req.error);
      };
    }));
  }
  return dbCache.get(dbName)!;
}

/**
 * Creates a namespaced app storage backed by IndexedDB.
 *
 * Using IndexedDB instead of localStorage ensures app data survives auth
 * resets, re-registrations, and logout flows — only explicit data wipe
 * operations should ever remove app content.
 */
export function createNamespacedStorage(namespace: string): AppStorage {
  return {
    async get<T>(key: string): Promise<T | null> {
      const db = await openAppDB(namespace);
      return new Promise((resolve, reject) => {
        const req = db.transaction('keyval', 'readonly').objectStore('keyval').get(key);
        req.onsuccess = () => resolve((req.result ?? null) as T | null);
        req.onerror = () => reject(req.error);
      });
    },
    async set<T>(key: string, value: T): Promise<void> {
      const db = await openAppDB(namespace);
      return new Promise((resolve, reject) => {
        const tx = db.transaction('keyval', 'readwrite');
        tx.objectStore('keyval').put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
    async remove(key: string): Promise<void> {
      const db = await openAppDB(namespace);
      return new Promise((resolve, reject) => {
        const tx = db.transaction('keyval', 'readwrite');
        tx.objectStore('keyval').delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

export * from './uuid';
export * from './crdtStorage';
export * from './storageProvider';
export * from './documentId';
export * from './profileResolver';

// ── Profile and Auth compatibility helpers (backed by localStorage) ───────────
const STORAGE_PREFIX = 'yada:account:';

export async function getActiveProfileId(): Promise<string | null> {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(`${STORAGE_PREFIX}activeProfileId`);
}

export async function setActiveProfileId(profileId: string): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${STORAGE_PREFIX}activeProfileId`, profileId);
}

function orderProfilesWithMainFirst(profileList: Profile[]): Profile[] {
  if (profileList.length <= 1) return profileList;
  const householdIdx = profileList.findIndex(p => p.profileType === 'household');
  if (householdIdx === -1) return profileList;

  let mainIdx = profileList.findIndex(p => p.profileType === 'standard');
  if (mainIdx === -1) mainIdx = profileList.findIndex(p => p.profileType === 'internal');
  if (mainIdx === -1) mainIdx = profileList.findIndex(p => p.profileType !== 'household');
  if (mainIdx === -1 || householdIdx >= mainIdx) return profileList;

  const result = [...profileList];
  const [mainProfile] = result.splice(mainIdx, 1);
  result.splice(householdIdx, 0, mainProfile);
  return result;
}

export async function getProfiles(): Promise<Profile[]> {
  if (typeof localStorage === 'undefined') return [];
  const raw = localStorage.getItem(`${STORAGE_PREFIX}profiles`);
  if (!raw) return [];
  try {
    const list = JSON.parse(raw) as Profile[];
    return orderProfilesWithMainFirst(list);
  } catch {
    return [];
  }
}

export async function setProfiles(profiles: Profile[]): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${STORAGE_PREFIX}profiles`, JSON.stringify(profiles));
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const activeId = await getActiveProfileId();
  if (!activeId) return null;
  const profiles = await getProfiles();
  return profiles.find((p) => p.id === activeId) || null;
}

export async function setActiveProfile(profileId: string): Promise<void> {
  await setActiveProfileId(profileId);
}

export async function hasHouseholdProfile(): Promise<boolean> {
  const profiles = await getProfiles();
  return profiles.some((p: Profile) => p.profileType === 'household');
}

export async function getHouseholdProfile(): Promise<Profile | null> {
  const profiles = await getProfiles();
  return profiles.find((p: Profile) => p.profileType === 'household') || null;
}

export async function getAccount(): Promise<Account | null> {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(`${STORAGE_PREFIX}account`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Account;
  } catch {
    return null;
  }
}

export async function getEnabledApps(): Promise<string[]> {
  const account = await getAccount();
  return account?.enabledApps || [];
}

export async function setAccount(account: Account): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${STORAGE_PREFIX}account`, JSON.stringify(account));
}

export async function removeAccount(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(`${STORAGE_PREFIX}account`);
}

export async function getSessionToken(): Promise<string | null> {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(`${STORAGE_PREFIX}session`);
}

export async function setSessionToken(token: string): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(`${STORAGE_PREFIX}session`, token);
}

export async function removeSessionToken(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(`${STORAGE_PREFIX}session`);
}

/**
 * Device-level values kept across logout and account changes. Only device
 * settings belong here, never account, profile or workspace data.
 * - `yada:deviceId` must stay paired with the device key pair in the
 *   `yada_device_keys` IndexedDB, which logout keeps; a new id would orphan it.
 * - `yada:defaultStorageUrl` is the storage node chosen on this device.
 */
export const DEVICE_LEVEL_KEYS: readonly string[] = ['yada:deviceId', 'yada:defaultStorageUrl'];

// App keys written before the `yada:` prefix convention.
const UNPREFIXED_USER_KEYS = ['notes:viewMode'];

// Kept across this purge: the `yada-llm-secrets` IndexedDB database holds only a
// device-level, non-extractable wrapping key; the encrypted API keys it protects
// are the `yada:llm:secrets` entry removed here.
/** Removes every account, profile, workspace and app value from localStorage. */
export async function clearAllUserData(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const keysToRemove: string[] = [...UNPREFIXED_USER_KEYS];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('yada:') || key.startsWith('yada-')) && !DEVICE_LEVEL_KEYS.includes(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

// BYOS (Bring Your Own Storage) exports
export * from './byos';
export * from './socialTrustManager';
